import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import AppSelect from "@/components/Core/AppSelect/AppSelect";
import { SegmentedControl } from "@/components/Core/SegmentedControl";
import { FormLabel } from "@/components/Core/AppText/AppText";
import { Section } from "@/components/Shared/Filters/filterControls";
import TagChip from "@/components/Shared/TagChip/TagChip";
import { InvoiceBatchFilter } from "@/models/InvoiceBatch";
import { CustomFieldDefinition } from "@/models/Organisation";
import {
	countCustomFieldConstraints,
	describeCustomFieldConstraints,
	getCustomFieldConstraint,
	parseCustomFieldConstraintValue,
	setCustomFieldConstraint,
} from "./invoiceBatchFilter.utils";

interface Props {
	filter: InvoiceBatchFilter;
	onChange: (filter: InvoiceBatchFilter) => void;
	/** The org's non-archived customer definitions (see `useCustomFieldDefinitions`). */
	definitions: CustomFieldDefinition[];
	/** Locked once the batch exists, matching the other filter groups. */
	disabled?: boolean;
}

/** Segment values for the boolean three-way choice. */
const BOOLEAN_ANY = "any";
const BOOLEAN_YES = "yes";
const BOOLEAN_NO = "no";

/**
 * The collapsed "Avancerat" group of the invoice batch filter: one equality
 * constraint per customer custom field. Booleans get an Alla | Ja | Nej
 * choice, strings and numbers a plain input where empty means "no
 * constraint". Renders nothing when the org has no customer custom fields.
 *
 * A locked batch shows its constraints as chips instead of controls, like
 * the selection chips of the neighbouring groups.
 */
const InvoiceBatchCustomFieldFilters: React.FC<Props> = ({
	filter,
	onChange,
	definitions,
	disabled,
}) => {
	const { t } = useTranslation();
	const count = countCustomFieldConstraints(filter);

	if (definitions.length === 0) return null;

	if (disabled) {
		const chips = describeCustomFieldConstraints(filter, definitions, t);
		if (!chips.length) return null;

		return (
			<div className="mb-5">
				<FormLabel>{t("invoiceBatch.filter.customFields")}</FormLabel>
				<div className="flex flex-wrap gap-1">
					{chips.map((chip) => (
						<TagChip key={chip.key} size="sm" name={chip.label} />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="mb-5">
			<Section title={t("invoiceBatch.filter.customFields")} count={count} flush>
				{definitions.map((definition) => {
					const constraint = getCustomFieldConstraint(filter, definition.key);

					if (definition.type === "boolean") {
						const selected =
							constraint === undefined ? BOOLEAN_ANY : constraint.value ? BOOLEAN_YES : BOOLEAN_NO;

						return (
							<SegmentedControl
								key={definition.key}
								label={definition.label.sv}
								skipTranslation
								size="sm"
								fullWidth
								value={selected}
								options={[
									{ value: BOOLEAN_ANY, label: t("ALL") },
									{ value: BOOLEAN_YES, label: t("YES") },
									{ value: BOOLEAN_NO, label: t("NO") },
								]}
								onChange={(value) =>
									onChange(
										setCustomFieldConstraint(
											filter,
											definition.key,
											value === BOOLEAN_ANY ? undefined : value === BOOLEAN_YES
										)
									)
								}
							/>
						);
					}

					if (definition.type === "select") {
						// Exact match on the option key; the empty option lifts the constraint.
						return (
							<div key={definition.key} className="mb-3">
								<AppSelect<{ id: string; name: string }>
									label={definition.label.sv}
									options={[
										{ id: "", name: t("ALL") },
										...(definition.options ?? []).map((option) => ({
											id: option.key,
											name: option.label.sv,
										})),
									]}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
									value={constraint === undefined ? "" : String(constraint.value)}
									onChange={(value) =>
										onChange(setCustomFieldConstraint(filter, definition.key, value || undefined))
									}
									margin="0"
								/>
							</div>
						);
					}

					return (
						<CustomFieldTextConstraint
							key={definition.key}
							definition={definition}
							value={constraint === undefined ? "" : String(constraint.value)}
							onCommit={(raw) =>
								onChange(
									setCustomFieldConstraint(
										filter,
										definition.key,
										parseCustomFieldConstraintValue(definition, raw)
									)
								)
							}
						/>
					);
				})}
			</Section>
		</div>
	);
};

/**
 * Text/number input that commits on blur or Enter rather than per keystroke,
 * so a half-typed number ("-", "1.") never collapses into "no constraint"
 * and re-clears the field mid-typing.
 */
const CustomFieldTextConstraint: React.FC<{
	definition: CustomFieldDefinition;
	value: string;
	onCommit: (raw: string) => void;
}> = ({ definition, value, onCommit }) => {
	const [draft, setDraft] = useState(value);

	useEffect(() => {
		setDraft(value);
	}, [value]);

	return (
		<AppInputNew
			label={definition.label.sv}
			skipLabelTranslation
			type={definition.type === "number" ? "number" : definition.type === "date" ? "date" : "text"}
			step={definition.type === "number" ? "any" : undefined}
			value={draft}
			onChangeText={setDraft}
			onBlurText={() => onCommit(draft)}
			onEnter={() => onCommit(draft)}
		/>
	);
};

export default InvoiceBatchCustomFieldFilters;
